import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import {
  PageContainer,
  PageHeader,
  Card,
  CardHeader,
  StatCard,
  AlertBanner,
  Button,
  Select,
  Input,
  FormGroup,
  Badge,
  Table,
  Thead,
  Th,
  Td,
  TRow,
  EmptyState,
  Divider,
} from "../ui";

// ============================================================
// Types (mirror backend/src/services/salesTaxService.ts)
// ============================================================

interface PeriodSummary {
  period: string;
  gross_sales: number;
  taxable_sales: number;
  tax_collected: number;
  tax_due: number;
  transactions: number;
  coverage_days: number;
  expected_days: number;
  complete: boolean;
  source: string;
}

interface Deadline {
  date: string;
  days_until: number;
  description: string;
  period_start: string;
  period_end: string;
}

interface PlcbClaim {
  id: string;
  po_id: string;
  purchase_date: string;
  tax_amount: number;
  status: "unfiled" | "filed" | "refunded";
  filing_period: string | null;
  refunded_amount: number | null;
  refunded_at: string | null;
  po_note: string | null;
  total_amount: number;
  invoice_amount: number | null;
}

interface PlcbNeedsEntry {
  po_id: string;
  purchase_date: string;
  total_amount: number;
  invoice_amount: number | null;
  po_note: string | null;
}

interface PlcbData {
  claims: PlcbClaim[];
  needs_entry: PlcbNeedsEntry[];
  totals: { unfiled: number; filed: number; refunded: number };
}

interface DashboardData {
  current_month: PeriodSummary;
  last_month: PeriodSummary;
  ytd: PeriodSummary;
  data_through: { mpower: string | null; native: string | null };
  filings: Filing[];
  next_deadline: Deadline;
  plcb: PlcbData;
  constants: { tax_rate: number; discount_rate: number; discount_cap_monthly: number };
}

interface NontaxableItem {
  sku_number: string;
  product_name: string;
  total: number;
  rows: number;
}

interface PlcbClaimSummary {
  id: string;
  purchase_date: string;
  tax_amount: number;
}

interface Preview {
  period: string;
  period_start: string;
  period_end: string;
  expected_days: number;
  coverage_days: number;
  missing_dates: string[];
  complete: boolean;
  source: string;
  sources: {
    mpower: { rows: number; rows_tax_estimated: number; days: number; gross_sales: number; tax_collected: number };
    native: { transactions: number; days: number; gross_sales: number; tax_collected: number };
  };
  gross_sales: number;
  nontaxable_sales: number;
  taxable_sales: number;
  tax_collected: number;
  tax_due: number;
  use_tax: number;
  discount_uncapped: number;
  discount_cap: number;
  discount_taken: number;
  tppr_credit: number;
  tppr_claims: PlcbClaimSummary[];
  net_tax_due: number;
  transaction_count: number;
  nontaxable_items: NontaxableItem[];
  notes: string[];
}

interface Filing {
  id: string;
  period: string;
  period_start: string;
  period_end: string;
  gross_sales: number;
  taxable_sales: number;
  nontaxable_sales: number;
  tax_collected: number;
  tax_due: number;
  discount_taken: number;
  tppr_credit: number;
  net_tax_due: number;
  status: "generated" | "filed" | "confirmed" | "superseded";
  filed_at: string | null;
  confirmation_number: string | null;
  coverage_days: number | null;
  expected_days: number | null;
  source: string;
  notes: string | null;
  created_at: string;
}

// ============================================================
// Helpers
// ============================================================

function fmt(n: number | string | null | undefined): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n) || 0);
}

function fmtDate(d: string | null | undefined): string {
  if (!d) return "--";
  const iso = d.length >= 10 ? d.slice(0, 10) : d;
  const [y, m, day] = iso.split("-").map(Number);
  if (!y || !m || !day) return d;
  return new Date(y, m - 1, day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function statusBadgeVariant(status: string): "default" | "gold" | "blue" | "green" | "danger" {
  switch (status) {
    case "generated":
    case "unfiled":
      return "blue";
    case "filed":
      return "gold";
    case "confirmed":
    case "refunded":
      return "green";
    default:
      return "default";
  }
}

function getLastMonth(): { month: string; year: string } {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return { month: String(d.getMonth() + 1).padStart(2, "0"), year: String(d.getFullYear()) };
}

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

function errorMessage(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: string } } };
  return e?.response?.data?.error || fallback;
}

// ============================================================
// Component
// ============================================================

export default function SalesTaxPage() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [filings, setFilings] = useState<Filing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const lastMonth = getLastMonth();
  const [genMonth, setGenMonth] = useState(lastMonth.month);
  const [genYear, setGenYear] = useState(lastMonth.year);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [allowPartial, setAllowPartial] = useState(false);
  const [generatedFiling, setGeneratedFiling] = useState<Filing | null>(null);

  const [markingId, setMarkingId] = useState<string | null>(null);
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [marking, setMarking] = useState(false);

  const [claimEntry, setClaimEntry] = useState<Record<string, string>>({});
  const [savingClaim, setSavingClaim] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [dashRes, filingsRes] = await Promise.all([api.get("/sales-tax/dashboard"), api.get("/sales-tax/filings")]);
      setDashboard(dashRes.data);
      setFilings(filingsRes.data);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Failed to load sales tax data"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const periodBounds = () => {
    const year = parseInt(genYear);
    const month = parseInt(genMonth);
    const lastDay = new Date(year, month, 0).getDate();
    return {
      period_start: `${year}-${genMonth}-01`,
      period_end: `${year}-${genMonth}-${String(lastDay).padStart(2, "0")}`,
    };
  };

  const handlePreview = async () => {
    const bounds = periodBounds();
    try {
      setPreviewing(true);
      setGeneratedFiling(null);
      const res = await api.get("/sales-tax/preview", { params: bounds });
      setPreview(res.data);
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Failed to compute figures"));
    } finally {
      setPreviewing(false);
    }
  };

  // Re-preview whenever the period changes
  useEffect(() => {
    handlePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genMonth, genYear]);

  const handleGenerate = async () => {
    const bounds = periodBounds();
    try {
      setGenerating(true);
      const res = await api.post("/sales-tax/generate", { ...bounds, allow_partial: allowPartial });
      setGeneratedFiling(res.data);
      setInfo(`PA-3 figures saved for ${res.data.period}. Download the keying sheet, file in myPATH, then mark as filed.`);
      setError("");
      await loadData();
    } catch (err) {
      setError(errorMessage(err, "Failed to generate PA-3 figures"));
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadPdf = (filingId: string) => {
    window.open(`/api/sales-tax/filings/${filingId}/pdf`, "_blank");
  };

  const handleMarkFiled = async (filingId: string) => {
    if (!confirmationNumber.trim()) return;
    try {
      setMarking(true);
      await api.post(`/sales-tax/filings/${filingId}/mark-filed`, { confirmation_number: confirmationNumber.trim() });
      setMarkingId(null);
      setConfirmationNumber("");
      setGeneratedFiling(null);
      setInfo("Filing recorded. PLCB credits on that return are now marked filed.");
      await loadData();
      await handlePreview();
    } catch (err) {
      setError(errorMessage(err, "Failed to mark filing as filed"));
    } finally {
      setMarking(false);
    }
  };

  const handleSaveClaim = async (poId: string) => {
    const raw = claimEntry[poId];
    const amount = Number(raw);
    if (!(amount >= 0) || raw === undefined || raw === "") return;
    try {
      setSavingClaim(poId);
      await api.post("/sales-tax/plcb-claims", { po_id: poId, tax_amount: amount, notes: "Entered on Sales Tax page" });
      setClaimEntry((prev) => ({ ...prev, [poId]: "" }));
      await loadData();
      await handlePreview();
    } catch (err) {
      setError(errorMessage(err, "Failed to save PLCB tax amount"));
    } finally {
      setSavingClaim(null);
    }
  };

  const handleRefunded = async (claimId: string) => {
    try {
      await api.post(`/sales-tax/plcb-claims/${claimId}/refunded`, {});
      await loadData();
    } catch (err) {
      setError(errorMessage(err, "Failed to mark claim refunded"));
    }
  };

  if (loading && !dashboard) {
    return (
      <PageContainer>
        <PageHeader title="Sales Tax" subtitle="PA-3 self-filing via myPATH" />
        <div className="text-center py-16 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
          Loading...
        </div>
      </PageContainer>
    );
  }

  const deadline = dashboard?.next_deadline;
  const deadlineVariant = deadline && deadline.days_until <= 7 ? "danger" : "gold";
  const currentYear = new Date().getFullYear();
  const yearOptions = [String(currentYear - 1), String(currentYear)];
  const plcb = dashboard?.plcb;
  const consts = dashboard?.constants;

  const renderMarkFiled = (filingId: string, compact = false) =>
    markingId === filingId ? (
      <div className="flex items-center gap-2">
        <Input value={confirmationNumber} onChange={setConfirmationNumber} placeholder="myPATH confirmation #" />
        <Button size="sm" onClick={() => handleMarkFiled(filingId)} disabled={marking || !confirmationNumber.trim()}>
          {marking ? "Saving..." : compact ? "OK" : "Confirm"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setMarkingId(null);
            setConfirmationNumber("");
          }}
        >
          {compact ? "X" : "Cancel"}
        </Button>
      </div>
    ) : compact ? (
      <button onClick={() => setMarkingId(filingId)} className="text-xs underline" style={{ color: "var(--rbw-blue-dark)" }}>
        Mark filed
      </button>
    ) : (
      <Button variant="gold" size="sm" onClick={() => setMarkingId(filingId)}>
        Mark as Filed
      </Button>
    );

  const line = (label: string, value: string, opts: { bold?: boolean; sub?: string; color?: string } = {}) => (
    <div className="flex justify-between items-start py-1 text-sm" style={{ borderBottom: "1px solid var(--rbw-border)" }}>
      <div>
        <div style={{ color: opts.bold ? "var(--rbw-navy)" : "var(--rbw-gray-dark)", fontWeight: opts.bold ? 700 : 400 }}>{label}</div>
        {opts.sub && (
          <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
            {opts.sub}
          </div>
        )}
      </div>
      <div className="tabular-nums" style={{ color: opts.color || "var(--rbw-navy)", fontWeight: opts.bold ? 700 : 500 }}>
        {value}
      </div>
    </div>
  );

  return (
    <PageContainer>
      <PageHeader title="Sales Tax" subtitle="PA-3 self-filing via myPATH (replaces the CPA filing)" />

      {error && (
        <AlertBanner variant="danger">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <span>{error}</span>
            <button onClick={() => setError("")} className="ml-auto underline text-xs">
              Dismiss
            </button>
          </div>
        </AlertBanner>
      )}
      {info && (
        <AlertBanner variant="gold">
          <div className="flex items-center gap-2 text-sm">
            <span>{info}</span>
            <button onClick={() => setInfo("")} className="ml-auto underline text-xs">
              Dismiss
            </button>
          </div>
        </AlertBanner>
      )}

      {/* 1. Deadline + data coverage */}
      {deadline && dashboard && (
        <AlertBanner variant={deadlineVariant}>
          <div className="text-sm">
            <div className="font-semibold">
              PA-3 due in {deadline.days_until} days ({fmtDate(deadline.date)}) -- {deadline.description}
            </div>
            <div className="text-xs mt-1">
              Sales data imported through: mPower {fmtDate(dashboard.data_through.mpower)}
              {dashboard.data_through.native ? `, Karen register ${fmtDate(dashboard.data_through.native)}` : ""}.
              {" "}Last month coverage: {dashboard.last_month.coverage_days}/{dashboard.last_month.expected_days} days
              {dashboard.last_month.complete ? " (complete)" : " -- import the missing mPower reports before filing"}.
            </div>
          </div>
        </AlertBanner>
      )}

      {/* 2. Dashboard */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            label={`Last Month Tax Due (${dashboard.last_month.period})`}
            value={fmt(dashboard.last_month.tax_due)}
            detail={`Taxable ${fmt(dashboard.last_month.taxable_sales)} | ${dashboard.last_month.coverage_days}/${dashboard.last_month.expected_days} days`}
            variant={dashboard.last_month.complete ? "gold" : "danger"}
          />
          <StatCard
            label="Current Month So Far"
            value={fmt(dashboard.current_month.tax_due)}
            detail={`Gross ${fmt(dashboard.current_month.gross_sales)} | through ${fmtDate(dashboard.data_through.mpower)}`}
            variant="blue"
          />
          <StatCard
            label="YTD Tax Due"
            value={fmt(dashboard.ytd.tax_due)}
            detail={`${dashboard.ytd.transactions.toLocaleString()} transactions`}
            variant="green"
          />
          <StatCard
            label="PLCB Credit Waiting"
            value={fmt(plcb?.totals.unfiled || 0)}
            detail={`Filed, awaiting refund: ${fmt(plcb?.totals.filed || 0)}`}
            variant="default"
          />
        </div>
      )}

      {/* 3. Figures for a period */}
      <Card className="mb-6">
        <CardHeader title="PA-3 Figures" />
        <p className="text-xs mb-3" style={{ color: "var(--rbw-gray-dark)" }}>
          Pick the month, check the numbers, then generate the keying sheet for myPATH.
        </p>

        <div className="flex items-end gap-4 mb-4 flex-wrap">
          <FormGroup label="Month">
            <Select value={genMonth} onChange={setGenMonth}>
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          </FormGroup>
          <FormGroup label="Year">
            <Select value={genYear} onChange={setGenYear}>
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </Select>
          </FormGroup>
          <Button variant="secondary" onClick={handlePreview} disabled={previewing}>
            {previewing ? "Computing..." : "Recompute"}
          </Button>
          <Button onClick={handleGenerate} disabled={generating || !preview || (!preview.complete && !allowPartial)}>
            {generating ? "Saving..." : "Generate PA-3 + Keying Sheet"}
          </Button>
          {preview && !preview.complete && (
            <label className="flex items-center gap-2 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
              <input type="checkbox" checked={allowPartial} onChange={(e) => setAllowPartial(e.target.checked)} />
              Generate a PROVISIONAL figure anyway (do not file it)
            </label>
          )}
        </div>

        {preview && (
          <>
            {!preview.complete && (
              <AlertBanner variant="danger">
                <div className="text-sm">
                  <div className="font-semibold">
                    {preview.missing_dates.length} of {preview.expected_days} days have no sales data. Import the mPower Detailed Sales report for those days before filing.
                  </div>
                  <div className="text-xs mt-1">Missing: {preview.missing_dates.slice(0, 12).join(", ")}{preview.missing_dates.length > 12 ? ` ... (+${preview.missing_dates.length - 12} more)` : ""}</div>
                </div>
              </AlertBanner>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  {preview.period} -- key into myPATH in this order
                </h3>
                {line("1. Gross sales", fmt(preview.gross_sales), { sub: "Product sales before tax; gift cards and keg deposits excluded" })}
                {line("2. Non-taxable sales", fmt(preview.nontaxable_sales), {
                  sub: preview.nontaxable_items.length
                    ? preview.nontaxable_items.slice(0, 3).map((i) => `${i.product_name} ${fmt(i.total)}`).join("; ")
                    : "None rung tax-free this period",
                })}
                {line("3. Net taxable sales", fmt(preview.taxable_sales), { bold: true })}
                {line(`4. Sales tax due (3 x ${((consts?.tax_rate || 0.06) * 100).toFixed(0)}%)`, fmt(preview.tax_due), {
                  bold: true,
                  sub: `Register collected ${fmt(preview.tax_collected)}`,
                })}
                {line("5. Use tax", fmt(preview.use_tax))}
                {line("6. Less: taxes paid on purchases resold (PLCB)", fmt(preview.tppr_credit), {
                  color: "var(--rbw-success)",
                  sub: preview.tppr_claims.length ? preview.tppr_claims.map((c) => `PLCB ${fmtDate(c.purchase_date)} ${fmt(c.tax_amount)}`).join("; ") : "No PLCB purchases queued",
                })}
                {line(`7. Less: vendor discount (1%, max ${fmt(preview.discount_cap)})`, fmt(preview.discount_taken), {
                  color: "var(--rbw-success)",
                  sub: preview.discount_uncapped > preview.discount_taken ? `1% would be ${fmt(preview.discount_uncapped)}; the monthly cap applies` : "Only if filed and paid by the due date",
                })}
                <div className="flex justify-between items-center pt-3">
                  <span className="text-base font-bold" style={{ color: "var(--rbw-navy)" }}>
                    Net amount to pay
                  </span>
                  <span className="text-lg font-bold tabular-nums" style={{ color: preview.net_tax_due < 0 ? "var(--rbw-danger)" : "var(--rbw-navy)" }}>
                    {fmt(preview.net_tax_due)}
                  </span>
                </div>
              </div>

              <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                <h3 className="text-sm font-bold mb-2" style={{ color: "var(--rbw-navy)" }}>
                  Where the numbers come from
                </h3>
                <p className="mb-1">
                  Coverage: {preview.coverage_days}/{preview.expected_days} days, {preview.transaction_count.toLocaleString()} transactions, source {preview.source}.
                </p>
                <p className="mb-1">
                  mPower import: {preview.sources.mpower.rows.toLocaleString()} lines over {preview.sources.mpower.days} days, gross {fmt(preview.sources.mpower.gross_sales)}, tax collected {fmt(preview.sources.mpower.tax_collected)}
                  {preview.sources.mpower.rows_tax_estimated > 0 ? ` (${preview.sources.mpower.rows_tax_estimated} lines with estimated tax)` : ""}.
                </p>
                {preview.sources.native.transactions > 0 && (
                  <p className="mb-1">
                    Karen register: {preview.sources.native.transactions} sales over {preview.sources.native.days} days, gross {fmt(preview.sources.native.gross_sales)}, tax {fmt(preview.sources.native.tax_collected)}.
                  </p>
                )}
                {preview.notes.length > 0 && (
                  <ul className="list-disc pl-4 mt-2 space-y-1">
                    {preview.notes.map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </>
        )}

        {generatedFiling && (
          <>
            <Divider />
            <div className="flex items-center gap-3 flex-wrap">
              <Badge variant={statusBadgeVariant(generatedFiling.status)}>{generatedFiling.status}</Badge>
              <span className="text-sm" style={{ color: "var(--rbw-navy)" }}>
                {generatedFiling.period}: net {fmt(generatedFiling.net_tax_due)}
              </span>
              <Button variant="secondary" size="sm" onClick={() => handleDownloadPdf(generatedFiling.id)}>
                Download keying sheet (PDF)
              </Button>
              {generatedFiling.status === "generated" && renderMarkFiled(generatedFiling.id)}
            </div>
          </>
        )}
      </Card>

      {/* 4. PLCB credits */}
      <Card className="mb-6" padding="p-0">
        <div className="p-6 pb-2">
          <CardHeader title="PLCB Sales Tax Paid (credit on the PA-3)" />
          <p className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
            Tax RBW paid PLCB on product bought for resale. Unfiled claims attach to the next return automatically; the refund arrives as a smaller payment.
          </p>
          {plcb && (
            <div className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
              Unfiled {fmt(plcb.totals.unfiled)} | Filed {fmt(plcb.totals.filed)} | Refunded {fmt(plcb.totals.refunded)}
            </div>
          )}
        </div>
        {plcb && plcb.needs_entry.length > 0 && (
          <div className="px-6 pb-3">
            <AlertBanner variant="gold">
              <div className="text-sm">
                <div className="font-semibold mb-1">{plcb.needs_entry.length} PLCB purchase(s) have no tax amount recorded. Enter the sales tax from the receipt:</div>
                {plcb.needs_entry.map((p) => (
                  <div key={p.po_id} className="flex items-center gap-2 mb-1 text-xs">
                    <span className="w-24">{fmtDate(p.purchase_date)}</span>
                    <span className="w-28">product {fmt(p.total_amount)}</span>
                    <span className="flex-1 truncate">{p.po_note || ""}</span>
                    <Input
                      value={claimEntry[p.po_id] || ""}
                      onChange={(v) => setClaimEntry((prev) => ({ ...prev, [p.po_id]: v }))}
                      placeholder="tax $"
                    />
                    <Button size="sm" onClick={() => handleSaveClaim(p.po_id)} disabled={savingClaim === p.po_id || !(Number(claimEntry[p.po_id]) >= 0) || !claimEntry[p.po_id]}>
                      Save
                    </Button>
                  </div>
                ))}
              </div>
            </AlertBanner>
          </div>
        )}
        {!plcb || plcb.claims.length === 0 ? (
          <EmptyState message="No PLCB claims yet" detail="Claims are created from PLCB purchase orders" icon="$" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Thead>
                <Th>Purchased</Th>
                <Th align="right">Product</Th>
                <Th align="right">Tax Paid</Th>
                <Th align="center">Status</Th>
                <Th>On Return</Th>
                <Th>Note</Th>
                <Th align="center">Refund</Th>
              </Thead>
              <tbody>
                {plcb.claims.map((c, i) => (
                  <TRow key={c.id} index={i}>
                    <Td bold>{fmtDate(c.purchase_date)}</Td>
                    <Td align="right">{fmt(c.total_amount)}</Td>
                    <Td align="right" bold>
                      {fmt(c.tax_amount)}
                    </Td>
                    <Td align="center">
                      <Badge variant={statusBadgeVariant(c.status)}>{c.status}</Badge>
                    </Td>
                    <Td>{c.filing_period || "--"}</Td>
                    <Td>
                      <span className="text-xs">{c.po_note || ""}</span>
                    </Td>
                    <Td align="center">
                      {c.status === "filed" ? (
                        <button onClick={() => handleRefunded(c.id)} className="text-xs underline" style={{ color: "var(--rbw-blue-dark)" }}>
                          Mark refunded
                        </button>
                      ) : c.status === "refunded" ? (
                        <span className="text-xs">{fmt(c.refunded_amount)} {fmtDate(c.refunded_at)}</span>
                      ) : (
                        "--"
                      )}
                    </Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>

      {/* 5. Filing history */}
      <Card padding="p-0">
        <div className="p-6 pb-0">
          <CardHeader title="Filing History" />
        </div>
        {filings.length === 0 ? (
          <EmptyState message="No filings yet" detail="Generate the first PA-3 above" icon="$" />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Thead>
                <Th>Period</Th>
                <Th align="right">Gross</Th>
                <Th align="right">Taxable</Th>
                <Th align="right">Tax Due</Th>
                <Th align="right">PLCB Credit</Th>
                <Th align="right">Discount</Th>
                <Th align="right">Net Paid</Th>
                <Th align="center">Coverage</Th>
                <Th align="center">Status</Th>
                <Th>Filed</Th>
                <Th>Confirmation #</Th>
                <Th align="center">Sheet</Th>
              </Thead>
              <tbody>
                {filings.map((f, i) => (
                  <TRow key={f.id} index={i}>
                    <Td bold>{f.period}</Td>
                    <Td align="right">{fmt(f.gross_sales)}</Td>
                    <Td align="right">{fmt(f.taxable_sales)}</Td>
                    <Td align="right">{fmt(f.tax_due)}</Td>
                    <Td align="right">{fmt(f.tppr_credit)}</Td>
                    <Td align="right">{fmt(f.discount_taken)}</Td>
                    <Td align="right" bold>
                      {fmt(f.net_tax_due)}
                    </Td>
                    <Td align="center">
                      {f.coverage_days != null ? `${f.coverage_days}/${f.expected_days}` : "--"}
                      {f.notes && f.notes.startsWith("PROVISIONAL") ? " (prov.)" : ""}
                    </Td>
                    <Td align="center">
                      <Badge variant={statusBadgeVariant(f.status)}>{f.status}</Badge>
                    </Td>
                    <Td>{fmtDate(f.filed_at)}</Td>
                    <Td>{f.confirmation_number || (f.status === "generated" ? renderMarkFiled(f.id, true) : "--")}</Td>
                    <Td align="center">
                      <button onClick={() => handleDownloadPdf(f.id)} className="p-1 rounded hover:bg-gray-100 transition-colors" title="Download keying sheet">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} style={{ color: "var(--rbw-navy)" }}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>
                    </Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card>
    </PageContainer>
  );
}
